// FLAGS: personal-data
// Reports every failed API call to Application Insights as an exception. This is the only path by which request
// URLs and response bodies leave the browser for telemetry, and Admin has no redaction helper of any kind.
import axios, { AxiosError } from "axios";
import { appInsights } from "@src/utils/appInsights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

export interface HttpErrorProperties {
  statusCode: number | undefined;
  url: string | undefined;
  method: string | undefined;
  responseData: string | undefined;
  errorMessage: string | undefined;
}

const shouldSkipTracking = (error: AxiosError): boolean =>
  !error.response || axios.isCancel(error);

const extractErrorMessage = (error: AxiosError): string | undefined =>
  JSON.stringify(error?.response?.data).substring(0, 500);

const getSeverityLevel = (status: number | undefined): SeverityLevel => {
  return status && status >= 500 ? SeverityLevel.Error : SeverityLevel.Warning;
};

const getResponseData = (error: AxiosError): string | undefined => {
  if (!error.response?.data) return undefined;
  try {
    return JSON.stringify(error.response.data).substring(0, 500);
  } catch {
    return undefined;
  }
};

// FLAGS: personal-data
// Sends the request path — client and consult ids included — and the first 500 characters of the error response
// body, twice, as `responseData` and `errorMessage`. Nothing is filtered or masked, so any personal or clinical
// detail an API error happens to echo back is stored in Application Insights for its retention period. Query-string
// values are not captured: the generated client keeps them in `config.params`, and only `config.url` is read here.
export const trackHttpError = (error: AxiosError): void => {
  if (shouldSkipTracking(error)) {
    return;
  }

  const properties: HttpErrorProperties = {
    statusCode: error.response?.status,
    url: error.config?.url,
    method: error.config?.method,
    responseData: getResponseData(error),
    errorMessage: extractErrorMessage(error),
  };

  try {
    appInsights.trackException({
      exception: new Error(
        `HTTP ${error.response?.status}: ${error.config?.url}`,
      ),
      properties,
      severityLevel: getSeverityLevel(error.response?.status),
    });
  } catch {
    // silently fail if tracking throws an error, to avoid impacting user experience
  }
};

export const httpErrorTrackingInterceptor = (
  error: AxiosError,
): Promise<never> => {
  trackHttpError(error);
  return Promise.reject(error);
};
